/**
 * Column field-type inference.
 *
 * Single entry point — `inferColumnMeta` — which receives:
 *   - column name
 *   - column index
 *   - the full set of rows (so we can scan for cardinality / patterns)
 *   - optional backend hints (sql/arrow type, semantic tag)
 *
 * Returns a `ColumnMeta` that completely describes how the renderer should
 * draw every cell in the column.
 */

import type { ColumnHints, ColumnMeta, FieldType } from './types';
import { CATEGORY_PALETTE_SIZE, paletteIndexFor } from './palette';

// ─────────────────────────────────────────────────────────────────────────────
// Header label
// ─────────────────────────────────────────────────────────────────────────────

const ACRONYM_MAP: Record<string, string> = {
  emv: 'eMV',
  vin: 'VIN',
  mv: 'MV',
  id: 'ID',
  url: 'URL',
  uri: 'URI',
  api: 'API',
  sql: 'SQL',
  ip: 'IP',
  uuid: 'UUID',
  arr: 'ARR',
  mrr: 'MRR',
  ltv: 'LTV',
  cac: 'CAC',
  kpi: 'KPI',
  utm: 'UTM',
  usd: 'USD',
  eur: 'EUR',
  gbp: 'GBP',
  inr: 'INR',
  jpy: 'JPY',
};

export function toLabel(raw: string): string {
  if (!raw) return '';
  // Already a human label? (Has spaces or mixed case.)
  if (raw.includes(' ') || /[A-Z]/.test(raw.slice(1))) {
    return raw;
  }
  return raw
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => {
      if (ACRONYM_MAP[word]) return ACRONYM_MAP[word];
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Regex / constant patterns
// ─────────────────────────────────────────────────────────────────────────────

// Strict-enough to reject JSON-wrapped values like `["foo@bar.com"]` —
// brackets, quotes, commas, etc. would all match a `[^\s@]+` group, so
// we restrict the local + domain parts to a conservative ASCII charset.
const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
const URL_RE = /^https?:\/\/[A-Za-z0-9.\-]+(?:[:/?#][^\s]*)?$/i;
// Airtable record ID (rec + 14 alphanum)
const AIRTABLE_RECID_RE = /^rec[A-Za-z0-9]{14}$/;
// UUID v1-v5
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Long alphanumeric token (Stripe ids, SHA hashes, etc.)
const LONG_HASH_RE = /^[A-Za-z0-9_-]{20,}$/;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

// Currency-suggestive name fragments. We match WORD-bounded segments only —
// i.e. `_amount_usd`, `total_revenue`, `unit_price`. We deliberately omit
// `total` (matches `total_score`) and `amount` (matches `amount_of_records`)
// when they appear without a money-related companion.
const STRONG_CURRENCY_NAME = [
  'price', 'cost', 'revenue', 'salary', 'budget', 'fee', 'fees',
  'arr', 'mrr', 'gmv', 'spend', 'spending', 'pay', 'payment', 'paid',
  'invoice', 'invoiced', 'balance', 'dollars', 'wage', 'wages',
];
const CURRENCY_CODE_SUFFIX = /(_usd|_eur|_gbp|_inr|_jpy|_aud|_cad|_chf|_cny)$/i;

// Percent-suggestive name fragments.
const PERCENT_NAME = [
  'pct', 'percent', 'ratio', '_rate', 'percentage', '_share', 'fraction',
];

// ID-suggestive name fragments / suffixes.
const ID_NAME_SUFFIXES = ['_id', '_uuid', '_uid'];
const ID_NAME_EQUALS = new Set(['id', 'uuid', 'uid', 'pk', 'guid']);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function nameContainsAny(lcName: string, fragments: string[]): boolean {
  for (const f of fragments) {
    if (lcName.includes(f)) return true;
  }
  return false;
}

function looksLikeArray(v: any): boolean {
  if (Array.isArray(v)) return true;
  if (typeof v === 'string') {
    const s = v.trim();
    return s.length > 1 && (s[0] === '[' || (s[0] === '{' && s[1] === '"'));
  }
  return false;
}

/**
 * True when an array holds at least one nested object/array item. Such arrays
 * have no sensible chip representation (each item would stringify to
 * "[object Object]"), so the column should render as `json` instead of
 * `multi_select`.
 */
function arrayHasObjectItems(arr: any[]): boolean {
  for (const item of arr) {
    if (item !== null && typeof item === 'object') return true;
  }
  return false;
}

/**
 * Normalise a raw item from an array to a display string, returning null
 * for values that should be treated as empty (SQL NULL, the string "null",
 * nested JSON arrays that unwrap to a single value, etc.)
 */
function _normaliseArrayItem(item: any): string | null {
  if (item === null || item === undefined) return null;
  // Recursively unwrap single-element JSON arrays: ["foo"] → "foo"
  if (Array.isArray(item)) {
    const inner = item.filter((x: any) => x !== null && x !== undefined && String(x) !== 'null');
    return inner.length === 1 ? String(inner[0]) : inner.length > 1 ? inner.map(String).join(', ') : null;
  }
  // Plain objects have no chip text — serialise to JSON rather than letting
  // String() collapse them to the useless "[object Object]".
  if (typeof item === 'object') {
    try {
      return JSON.stringify(item);
    } catch {
      return null;
    }
  }
  const s = String(item).trim();
  if (s === 'null' || s === '') return null;
  // Unwrap a single-element JSON array string: '["foo"]' → 'foo'
  if (s.startsWith('[') && s.endsWith(']')) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        const inner = parsed.filter((x: any) => x !== null && x !== undefined && String(x) !== 'null');
        if (inner.length === 0) return null;
        return inner.length === 1 ? String(inner[0]) : inner.map(String).join(', ');
      }
    } catch { /* not JSON, use as-is */ }
  }
  return s;
}

/** Try to coerce a value that *might* be a JSON-encoded array into an array of display strings. */
export function unwrapArrayLike(v: any): any[] | null {
  if (Array.isArray(v)) {
    const items = v.map(_normaliseArrayItem).filter((x): x is string => x !== null);
    return items.length > 0 ? items : null;
  }
  if (typeof v === 'string') {
    const s = v.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          const items = parsed.map(_normaliseArrayItem).filter((x): x is string => x !== null);
          return items.length > 0 ? items : null;
        }
      } catch {
        // Not valid JSON — check if it's a STRING_AGG of JSON arrays:
        // e.g. '["Series A"], ["Series B"], [null]'
        // Split on '], [' pattern and try to unwrap each segment.
        if (s.includes('], [') || s.includes('],[')) {
          const segments = s.split(/\],\s*\[/);
          const items: string[] = [];
          for (const seg of segments) {
            const cleaned = seg.replace(/^\[/, '[').replace(/\]$/, ']');
            const attempt = _normaliseArrayItem(cleaned.startsWith('[') ? cleaned : `[${cleaned}]`);
            if (attempt !== null) items.push(attempt);
          }
          return items.length > 0 ? items : null;
        }
      }
    }
  }
  return null;
}

function arrowTypeToFieldType(t: string | undefined): FieldType | undefined {
  if (!t) return undefined;
  const lc = t.toLowerCase();
  // Lists of nested structures (list<struct>, array<map>, …) can't be chips —
  // each element would render as "[object Object]". Treat them as JSON.
  if (/^(list|array)\s*[<[]\s*(struct|map|list|array|json|object)/.test(lc)) {
    return 'json';
  }
  if (
    lc.startsWith('list<') || lc === 'list' || lc.startsWith('list[') ||
    lc.startsWith('array<') || lc.startsWith('array[')
  ) {
    return 'multi_select';
  }
  if (
    lc.startsWith('struct<') || lc.startsWith('map<') ||
    lc === 'json' || lc === 'object'
  ) {
    return 'json';
  }
  if (
    lc === 'bool' || lc === 'boolean' ||
    lc === 'bit' || lc === 'tinyint(1)'
  ) {
    return 'boolean';
  }
  if (lc.includes('timestamp') || lc.includes('datetime')) return 'datetime';
  if (lc === 'date') return 'date';
  if (
    lc.includes('int') || lc === 'bigint' || lc === 'smallint' || lc === 'tinyint' ||
    lc.includes('float') || lc.includes('double') || lc.includes('decimal') ||
    lc.includes('numeric') || lc === 'real'
  ) {
    return 'number';
  }
  if (
    lc === 'varchar' || lc === 'char' || lc === 'string' || lc === 'text' ||
    lc === 'utf8' || lc === 'utf-8' || lc === 'large_string' ||
    lc.startsWith('varchar(') || lc.startsWith('char(')
  ) {
    return 'text';
  }
  return undefined;
}

function semanticToMeta(
  semantic: string | undefined,
): { type: FieldType; currency?: string; percentScale?: 'fraction' | 'whole' } | undefined {
  if (!semantic) return undefined;
  const lc = semantic.toLowerCase();
  if (lc === 'currency' || lc === 'money') return { type: 'currency', currency: 'USD' };
  if (lc.startsWith('currency_')) {
    const code = lc.slice('currency_'.length).toUpperCase();
    return { type: 'currency', currency: code || 'USD' };
  }
  if (lc === 'percent' || lc === 'percentage') return { type: 'percent', percentScale: 'whole' };
  if (lc === 'percent_fraction' || lc === 'ratio') return { type: 'percent', percentScale: 'fraction' };
  if (lc === 'email') return { type: 'email' };
  if (lc === 'url' || lc === 'link') return { type: 'url' };
  if (lc === 'record_id' || lc === 'id') return { type: 'id' };
  if (lc === 'categorical' || lc === 'select' || lc === 'single_select') return { type: 'categorical' };
  if (lc === 'multi_select' || lc === 'tags' || lc === 'array') return { type: 'multi_select' };
  if (lc === 'date') return { type: 'date' };
  if (lc === 'datetime' || lc === 'timestamp') return { type: 'datetime' };
  if (lc === 'long_text' || lc === 'text_long') return { type: 'long_text' };
  if (lc === 'json' || lc === 'object') return { type: 'json' };
  if (lc === 'boolean' || lc === 'bool') return { type: 'boolean' };
  if (lc === 'number' || lc === 'numeric') return { type: 'number' };
  return undefined;
}

interface SampleStats {
  total: number;
  nonNull: number;
  numbers: number;
  booleans: number;
  arrays: number;
  /** Arrays whose items are themselves objects/arrays (nested, non-primitive). */
  complexArrays: number;
  objects: number;
  emails: number;
  urls: number;
  recIds: number;
  uuids: number;
  longHashes: number;
  isoDates: number;
  isoDateTimes: number;
  longText: number;
  distinct: Map<string, number>;
  totalLen: number;
  maxLen: number;
}

function gatherStats(rows: any[][], colIdx: number, sampleLimit: number = 200): SampleStats {
  const stats: SampleStats = {
    total: rows.length,
    nonNull: 0,
    numbers: 0,
    booleans: 0,
    arrays: 0,
    complexArrays: 0,
    objects: 0,
    emails: 0,
    urls: 0,
    recIds: 0,
    uuids: 0,
    longHashes: 0,
    isoDates: 0,
    isoDateTimes: 0,
    longText: 0,
    distinct: new Map(),
    totalLen: 0,
    maxLen: 0,
  };

  const limit = Math.min(rows.length, sampleLimit);
  for (let i = 0; i < limit; i++) {
    const v = rows[i]?.[colIdx];
    if (v === null || v === undefined || v === 'null') continue;
    stats.nonNull++;

    if (Array.isArray(v)) {
      stats.arrays++;
      if (arrayHasObjectItems(v)) stats.complexArrays++;
      const key = JSON.stringify(v);
      stats.distinct.set(key, (stats.distinct.get(key) || 0) + 1);
      continue;
    }
    if (typeof v === 'number') {
      stats.numbers++;
      continue;
    }
    if (typeof v === 'boolean') {
      stats.booleans++;
      continue;
    }
    if (typeof v === 'object') {
      stats.objects++;
      continue;
    }
    if (typeof v === 'string') {
      const s = v;
      stats.totalLen += s.length;
      if (s.length > stats.maxLen) stats.maxLen = s.length;
      // Distinct counter — cap key length so giant blobs don't blow memory.
      const key = s.length > 100 ? s.slice(0, 100) : s;
      stats.distinct.set(key, (stats.distinct.get(key) || 0) + 1);

      if (s.length > 120) stats.longText++;
      // JSON-array-encoded strings count as arrays — and ONLY arrays.
      // If a value parses as an array it must never also count as email /
      // url / id, otherwise `["foo@bar.com"]` ends up tagged `email`.
      // Also handles STRING_AGG output of JSON-array columns, which looks
      // like '["A"], ["B"], [null]' — not valid JSON but clearly array-like.
      let isJsonArray = false;
      if (s.length > 1 && s[0] === '[' && s[s.length - 1] === ']') {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) {
            stats.arrays++;
            if (arrayHasObjectItems(parsed)) stats.complexArrays++;
            isJsonArray = true;
          }
        } catch {
          // Not valid JSON — check if it's a STRING_AGG of JSON-array values:
          // e.g. '["Series A"], ["Series B"], [null]'
          if (s.includes('], [') || s.includes('],[')) {
            stats.arrays++;
            isJsonArray = true;
          }
        }
      }
      if (!isJsonArray) {
        if (EMAIL_RE.test(s)) stats.emails++;
        else if (URL_RE.test(s)) stats.urls++;
        else if (AIRTABLE_RECID_RE.test(s)) stats.recIds++;
        else if (UUID_RE.test(s)) stats.uuids++;
        else if (s.length >= 20 && s.length <= 64 && LONG_HASH_RE.test(s)) stats.longHashes++;

        if (ISO_DATETIME_RE.test(s)) stats.isoDateTimes++;
        else if (ISO_DATE_RE.test(s)) stats.isoDates++;
      }
    }
  }
  return stats;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main entry
// ─────────────────────────────────────────────────────────────────────────────

/** Maximum distinct text values for a column to count as `categorical`. */
export const CATEGORICAL_MAX_DISTINCT = 24;
/** Maximum average string length for a column to be a categorical chip column. */
const CATEGORICAL_MAX_AVG_LEN = 32;
/** Above this average length, plain `text` becomes `long_text` (multi-line). */
const LONG_TEXT_AVG_LEN = 80;

export function inferColumnMeta(
  name: string,
  colIdx: number,
  rows: any[][],
  hints?: ColumnHints,
): ColumnMeta {
  const lcName = (name || '').toLowerCase();
  const stats = gatherStats(rows, colIdx);
  const allNull = stats.total > 0 && stats.nonNull === 0;

  // 1. Backend semantic hint — highest priority.
  const semantic = hints?.columnSemantics?.[name];
  const fromSemantic = semanticToMeta(semantic);

  // 2. Backend SQL/Arrow type → coarse type.
  const sqlType = hints?.columnTypes?.[name];
  const fromSql = arrowTypeToFieldType(sqlType);

  // Resolve type --------------------------------------------------------------
  let type: FieldType;
  let currency: string | undefined;
  let percentScale: 'fraction' | 'whole' | undefined;

  if (fromSemantic) {
    type = fromSemantic.type;
    currency = fromSemantic.currency;
    percentScale = fromSemantic.percentScale;
    // A column tagged as a fraction (0–1) but holding 9.55 is already a whole
    // percent; trusting the tag would show 955%. Older saved results carry it.
    if (percentScale === 'fraction') {
      const lim = Math.min(rows.length, 100);
      for (let i = 0; i < lim; i++) {
        const n = Number(rows[i]?.[colIdx]);
        if (Number.isFinite(n) && Math.abs(n) > 1.001) {
          percentScale = 'whole';
          break;
        }
      }
    }
  } else if (fromSql) {
    type = fromSql;
  } else {
    type = 'text';
  }

  // Refine numeric columns -------------------------------------------------
  if (type === 'number') {
    // Currency promotion (very conservative — strict name match OR explicit currency-code suffix).
    const isCurrencyByName =
      CURRENCY_CODE_SUFFIX.test(lcName) ||
      nameContainsAny(lcName, STRONG_CURRENCY_NAME) ||
      // `_amount` is currency-y *only* if combined with other money words.
      // Avoid bare `amount` fragments — `count_amount` is not money.
      /(^|_)amount(_|$)/.test(lcName) && /usd|eur|gbp|inr|jpy|cost|price|paid|fee|invoice/.test(lcName);
    if (isCurrencyByName) {
      type = 'currency';
      const m = lcName.match(CURRENCY_CODE_SUFFIX);
      currency = (m ? m[1].slice(1) : 'USD').toUpperCase();
    }
    // Percent promotion.
    if (type === 'number' && nameContainsAny(lcName, PERCENT_NAME)) {
      type = 'percent';
      // Heuristic: max abs value <= 1 → fraction; otherwise whole.
      let maxAbs = 0;
      const lim = Math.min(rows.length, 100);
      for (let i = 0; i < lim; i++) {
        const v = rows[i]?.[colIdx];
        if (typeof v === 'number') {
          const a = Math.abs(v);
          if (a > maxAbs) maxAbs = a;
        }
      }
      percentScale = maxAbs <= 1.001 ? 'fraction' : 'whole';
    }
  }

  // Refine text columns -----------------------------------------------------
  if (type === 'text' && stats.nonNull > 0) {
    const arrayShare = stats.arrays / stats.nonNull;
    const objectShare = stats.objects / stats.nonNull;
    const emailShare = stats.emails / stats.nonNull;
    const urlShare = stats.urls / stats.nonNull;
    const idShare = (stats.recIds + stats.uuids + stats.longHashes) / stats.nonNull;
    const isoDateShare = stats.isoDates / stats.nonNull;
    const isoDateTimeShare = stats.isoDateTimes / stats.nonNull;

    if (objectShare >= 0.5) {
      // Raw objects ({...}) have no chip/text representation — render as JSON.
      type = 'json';
    } else if (arrayShare >= 0.5) {
      // Arrays of nested objects can't be chips (each item would stringify to
      // "[object Object]"); show them as collapsible JSON instead. Arrays of
      // primitives stay as multi-select chips.
      type = stats.complexArrays / stats.arrays >= 0.5 ? 'json' : 'multi_select';
    } else if (emailShare >= 0.7) {
      type = 'email';
    } else if (urlShare >= 0.7) {
      type = 'url';
    } else if (idShare >= 0.7) {
      type = 'id';
    } else if (isoDateTimeShare >= 0.7) {
      type = 'datetime';
    } else if (isoDateShare >= 0.7) {
      type = 'date';
    } else {
      // Categorical detection — small distinct set of text values.
      const distinctCount = stats.distinct.size;
      const avgLen = stats.nonNull ? stats.totalLen / stats.nonNull : 0;
      if (
        distinctCount >= 2 &&
        distinctCount <= CATEGORICAL_MAX_DISTINCT &&
        avgLen <= CATEGORICAL_MAX_AVG_LEN
      ) {
        type = 'categorical';
      } else if (avgLen >= LONG_TEXT_AVG_LEN) {
        type = 'long_text';
      }
    }

    // Override: column-name suffixes that strongly imply ID semantics.
    if (
      type !== 'multi_select' &&
      type !== 'email' &&
      type !== 'url' &&
      (ID_NAME_EQUALS.has(lcName) || ID_NAME_SUFFIXES.some((s) => lcName.endsWith(s)))
    ) {
      // Treat short categorical-looking IDs as `id` so we render them
      // monospaced + truncated rather than as colorful chips.
      type = 'id';
    }
  }

  // Build category index ----------------------------------------------------
  // Two goals:
  //   1. Distinct values within a column should never collide on color
  //      (round-robin through the palette).
  //   2. The same query should produce the same colors on every reload
  //      (sort values alphabetically, then seed the per-column starting
  //      offset from a hash of the column name).
  // Falls back to wrap-around when a column has more than 15 distinct values.
  let categoryIndex: Map<string, number> | undefined;
  if (type === 'categorical' || type === 'multi_select') {
    categoryIndex = new Map();
    const distinctValues: string[] = [];
    if (type === 'categorical') {
      Array.from(stats.distinct.keys()).forEach((k) => distinctValues.push(k));
    } else {
      const seen = new Set<string>();
      for (let i = 0; i < rows.length; i++) {
        const v = rows[i]?.[colIdx];
        const arr = unwrapArrayLike(v);
        if (!arr) continue;
        for (const item of arr) {
          const key = typeof item === 'string' ? item : String(item);
          if (!seen.has(key)) {
            seen.add(key);
            distinctValues.push(key);
          }
        }
      }
    }
    distinctValues.sort();

    const usable = CATEGORY_PALETTE_SIZE - 1; // skip palette[0] (slate fallback)
    const startOffset = (paletteIndexFor(name) - 1 + usable) % usable; // 0..usable-1
    distinctValues.forEach((v, i) => {
      const idx = 1 + ((startOffset + i) % usable);
      categoryIndex!.set(v, idx);
    });
  }

  // Alignment + width -------------------------------------------------------
  const align: 'left' | 'right' | 'center' =
    type === 'number' || type === 'currency' || type === 'percent' ? 'right' :
    type === 'boolean' ? 'center' :
    'left';

  const suggestedWidth = computeSuggestedWidth(type, name, stats);

  return {
    name,
    label: toLabel(name),
    type,
    currency,
    percentScale,
    categoryIndex,
    align,
    suggestedWidth,
    allNull,
  };
}

function computeSuggestedWidth(type: FieldType, name: string, stats: SampleStats): number {
  const labelLen = name.length;
  switch (type) {
    case 'boolean': return 80;
    case 'number': return 120;
    case 'currency': return 140;
    case 'percent': return 110;
    case 'date': return 130;
    case 'datetime': return 170;
    case 'id': return 140;
    case 'email': return 220;
    case 'url': return 220;
    case 'long_text': return 320;
    case 'json': return 160;
    case 'multi_select': {
      // Categorical width scales with how many tags we expect and avg length.
      const avg = stats.nonNull ? stats.totalLen / stats.nonNull : 12;
      return Math.max(180, Math.min(360, Math.round(avg * 4 + 60)));
    }
    case 'categorical': {
      // Width ~= max distinct value length with chip padding.
      // (Array.from to dodge --downlevelIteration; cap at 50 so we don't
      // walk huge category sets unnecessarily.)
      let maxLen = labelLen;
      const keys = Array.from(stats.distinct.keys());
      const limit = Math.min(keys.length, 50);
      for (let i = 0; i < limit; i++) {
        const k = keys[i];
        if (k.length > maxLen) maxLen = k.length;
      }
      return Math.max(120, Math.min(260, maxLen * 9 + 48));
    }
    case 'null': return 80;
    case 'text':
    default: {
      const avg = stats.nonNull ? stats.totalLen / stats.nonNull : 12;
      return Math.max(120, Math.min(280, Math.round(avg * 7 + 24)));
    }
  }
}
